import { assertNever } from '@sparkdotfi/common-universal'

import { CheckEligibilityViewProps } from '../views/CheckEligibilityView'
import { DashboardViewProps } from '../views/DashboardView'
import { useCheckEligibilityViewProps } from './useCheckEligibilityViewProps'
import { useDashboardViewProps } from './useDashboardViewProps'
import { usePrefetchDialogQueries } from './usePrefetchDialogQueries'
import { useSpkAirdropData } from './useSpkAirdropData'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useOpenStakeWithDelegateDialog } from '@/features/spk/staking/dialogs/stake/useOpenStakeWithDelegateDialog'

export type UseSpkAirdropResult =
  | {
      view: 'check-eligibility'
      checkEligibilityViewProps: CheckEligibilityViewProps
    }
  | {
      view: 'airdrop-dashboard'
      airdropDashboardViewProps: DashboardViewProps
    }

export function useSpkAirdrop(): UseSpkAirdropResult {
  const { account: address } = useConnectedAddress({ throwOnNotConnected: false })
  const { chainId } = usePageChainId()

  usePrefetchDialogQueries({ chainId })
  const { accountAirdropSummary, accountAirdropClaims, accountSpkStakedAmount } = useSpkAirdropData({ chainId })
  const currentAirdropView =
    address && accountAirdropSummary.totalAllocation.gt(0) ? 'airdrop-dashboard' : 'check-eligibility'

  const openStakeSpkDialog = useOpenStakeWithDelegateDialog()

  const checkEligibilityViewProps = useCheckEligibilityViewProps({
    accountAirdropSummary,
    chainId,
  })

  const dashboardViewProps = useDashboardViewProps({
    accountAirdropSummary,
    accountAirdropClaims,
    accountSpkStakedAmount,
    chainId,
    openStakeSpkDialog,
  })

  switch (currentAirdropView) {
    case 'check-eligibility':
      return {
        view: currentAirdropView,
        checkEligibilityViewProps,
      }
    case 'airdrop-dashboard':
      return {
        view: currentAirdropView,
        airdropDashboardViewProps: dashboardViewProps,
      }
    default:
      assertNever(currentAirdropView)
  }
}
